import { Alert, Button, Card, Descriptions, Select, Space, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Screening = {
  id: string;
  screening_no: string;
  screened_on: string;
  framework_code: string;
  domain: string;
  outcome: string;
  summary: string;
  detail_text?: string | null;
  status: string;
  record_version: number;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  section_name?: string;
};

export default function InclusionScreeningDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.inclusion.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-inclusion-screening', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Screening>(`/education/inclusion/screenings/${id}`)).data,
  });
  const row = query.data;
  const open = row && row.status === 'open';

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/inclusion/screenings/${id}/status`, {
        status,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('screeningStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-inclusion-screening', id] });
      queryClient.invalidateQueries({ queryKey: ['education-inclusion-screenings'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.screening_no || t('inclusionScreenings')}
      description={t('inclusionScreeningsIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/inclusion/screenings')}>{t('inclusionScreenings')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="warning" showIcon message={t('inclusionNonDiagnosticHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('student')}>
                {`${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label={t('section')}>{row.section_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('screenedOn')}>{row.screened_on}</Descriptions.Item>
              <Descriptions.Item label={t('framework')}>{row.framework_code}</Descriptions.Item>
              <Descriptions.Item label={t('screeningDomain')}>{row.domain}</Descriptions.Item>
              <Descriptions.Item label={t('screeningOutcome')}>{row.outcome}</Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {row.summary}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail')} span={2}>
                {row.detail_text || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        {canManage && open ? (
          <Card size="small" title={t('status')}>
            <Select
              style={{ minWidth: 200 }}
              placeholder={t('status')}
              options={[
                { value: 'closed', label: t('statusClosed') },
                { value: 'cancelled', label: t('statusCancelled') },
              ]}
              onChange={(v) => setStatus.mutate(v)}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
